import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLocalSessionId,
  useParticipantIds,
  useParticipantProperty,
  useScreenShare,
} from '@daily-co/daily-react';
import { useSubjectLanguageVariants } from '@features/user';
import { PipExitIcon } from '@icons/PipExitIcon';
import { PipIcon } from '@icons/PipIcon';
import {
  ArrowBack as ArrowBackIcon,
  ChatBubbleOutlined as ChatIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Logout as LogoutIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  PeopleAlt as PeopleIcon,
  ScreenShare as ScreenShareIcon,
  StopScreenShare as StopScreenShareIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
} from '@mui/icons-material';
import { Badge, Box, ButtonBase, Stack, Tooltip, Typography } from '@mui/material';
import type { AudioInputDeviceService } from '@services/audio';
import type { DailyCallService } from '@services/dailycall';
import { useDailyCallState } from '@services/dailycall';
import SentryService from '@services/SentryService';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { useTelehealthCallManager } from '../../context/useTelehealthCallManager';
import { useCallLayout } from '../../hooks/useCallLayout';
import {
  clearWaitingRoomLaunchIntent,
  selectChatMessages,
  selectChatUnreadCount,
  selectHideSelfView,
  selectIsChatPanelOpen,
  selectWaitingRoomLaunchIntent,
  setChatPanelOpen,
  toggleChatPanel,
} from '../../store';
import type { ManualLayoutChoice, TelehealthSession } from '../../types';
import type {
  ProviderWaitingRoomRemoveRequest,
  WaitingRoomEntry,
  WaitingRoomProviderAvailability,
} from '../../waiting-room';
import {
  getWaitingRoomAdmitErrorMessage,
  getWaitingRoomRemoveErrorMessage,
  WaitingRoomEntryActionButtons,
  WaitingRoomPeoplePanel,
  WaitingRoomRemoveDialog,
} from '../../waiting-room';
import { CameraDropdown } from '../CameraDropdown';
import { ChatPanel } from '../ChatPanel';
import { GridView } from '../GridView';
import { LayoutToggle } from '../LayoutToggle';
import { MicDropdown } from '../MicDropdown';
import { RecordingStatusBar } from '../RecordingStatusBar';
import { SpeakerView } from '../SpeakerView';
import { SplitMediaButton } from '../SplitMediaButton';
import { VideoSpotlight } from '../VideoSpotlight';

interface PipViewProps {
  dailyCallService: DailyCallService;
  audioInputDeviceService: AudioInputDeviceService | null;
  setSelectedMicrophone: (mic: MediaDeviceInfo) => void;
  pipWindow?: Window;
  isRecording: boolean;
  isRecordingStarting?: boolean;
  isRecordingPaused?: boolean;
  session: TelehealthSession;
  onLeave: () => void;
  onClose?: () => void;
  isPipWindow?: boolean;
  onEnterPip?: () => void;
  isPipSupported?: boolean;
  onPauseRecording?: () => void;
  onResumeRecording?: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  compactControls?: boolean;
  /** Container element for popovers — needed in fullscreen mode where portals to document.body are hidden */
  popoverContainerEl?: HTMLElement | null;
}

const isAdmittableEntry = (entry: WaitingRoomEntry) =>
  entry.current_state === 'DEVICE_CHECK' || entry.current_state === 'IN_QUEUE';

const isWaitingRoomListEntry = (entry: WaitingRoomEntry) =>
  entry.current_state === 'CHECKED_IN' ||
  entry.current_state === 'DEVICE_CHECK' ||
  entry.current_state === 'IN_QUEUE';

const getAdmitActionLabel = (entry: WaitingRoomEntry) => {
  if (isAdmittableEntry(entry)) {
    return 'Add to current call';
  }
  if (entry.current_state === 'CHECKED_IN') {
    return 'Check-in in progress';
  }
  return 'Already admitted';
};

export function PipView({
  dailyCallService,
  audioInputDeviceService,
  setSelectedMicrophone,
  pipWindow,
  isRecording,
  isRecordingStarting,
  isRecordingPaused = false,
  session,
  onLeave,
  onClose,
  isPipWindow = false,
  onEnterPip,
  isPipSupported = false,
  onPauseRecording,
  onResumeRecording,
  onToggleFullscreen,
  isFullscreen = false,
  compactControls = false,
  popoverContainerEl,
}: PipViewProps) {
  const callState = useDailyCallState(dailyCallService);
  const remoteParticipantIds = useParticipantIds({ filter: 'remote' });
  const localSessionId = useLocalSessionId();
  const localUserName = useParticipantProperty(localSessionId ?? '', 'user_name');
  const hideSelfView = useAppSelector(selectHideSelfView);
  const layout = useCallLayout(hideSelfView);
  const { telehealth } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const subjectLanguage = useSubjectLanguageVariants();
  const { isSharingScreen, screens, startScreenShare, stopScreenShare } = useScreenShare();

  const { sendChatMessage, waitingRoom } = useTelehealthCallManager();
  const dispatch = useAppDispatch();
  const chatMessages = useAppSelector(selectChatMessages);
  const chatUnreadCount = useAppSelector(selectChatUnreadCount);
  const isChatPanelOpen = useAppSelector(selectIsChatPanelOpen);
  const waitingRoomLaunchIntent = useAppSelector(selectWaitingRoomLaunchIntent);

  const [activeDropdown, setActiveDropdown] = useState<'mic' | 'camera' | null>(null);
  const [isPeoplePanelOpen, setIsPeoplePanelOpen] = useState(false);
  const [isCopyingWaitingRoomLink, setIsCopyingWaitingRoomLink] = useState(false);
  const [waitingRoomLinkCopied, setWaitingRoomLinkCopied] = useState(false);
  const [entryPendingRemoval, setEntryPendingRemoval] = useState<WaitingRoomEntry | null>(null);
  const hasAutoOpenedPeoplePanelRef = useRef(false);
  const someoneElseSharing = screens.length > 0 && !isSharingScreen;

  // Responsive button sizing
  const btnSize = compactControls ? 36 : 48;
  const iconSize = compactControls ? 16 : 20;
  const waitingRoomEntries = waitingRoom.entries.filter(isWaitingRoomListEntry);
  const waitingCount = waitingRoomEntries.length;
  const defaultExpandedWaitingRoomEntryId =
    waitingRoomLaunchIntent && waitingRoomLaunchIntent.encounterId === session.encounterId
      ? waitingRoomLaunchIntent.entryId
      : undefined;
  const shouldShowWaitingRoomControls = waitingRoom.hasAccess && !isPipWindow;
  const canShowPeopleRail = shouldShowWaitingRoomControls && isPeoplePanelOpen && !compactControls;
  const canShowPeopleOverlay =
    shouldShowWaitingRoomControls && isPeoplePanelOpen && compactControls;
  const inCallParticipants = remoteParticipantIds.map((participantId, index) => {
    const dailyParticipantName = callState.remoteParticipantNames.get(participantId);

    return {
      id: participantId,
      name: dailyParticipantName ?? `Participant ${index + 1}`,
      subtitle: 'In call',
    };
  });

  useEffect(() => {
    if (!shouldShowWaitingRoomControls) {
      return;
    }

    if (waitingCount === 0) {
      hasAutoOpenedPeoplePanelRef.current = false;
      return;
    }

    if (!hasAutoOpenedPeoplePanelRef.current && !compactControls) {
      hasAutoOpenedPeoplePanelRef.current = true;
      setIsPeoplePanelOpen(true);
    }
  }, [compactControls, shouldShowWaitingRoomControls, waitingCount]);

  const handleSetWaitingRoomAvailability = useCallback(
    async (availability: WaitingRoomProviderAvailability) => {
      try {
        await waitingRoom.setAvailability(availability);
      } catch {
        enqueueSnackbar('Could not update waiting room availability.', { variant: 'error' });
      }
    },
    [enqueueSnackbar, waitingRoom],
  );

  const handleCopyWaitingRoomLink = useCallback(async () => {
    const shortUrl = waitingRoom.link?.short_url;
    if (!shortUrl) {
      enqueueSnackbar('Waiting room link is not available yet.', { variant: 'warning' });
      return;
    }

    setIsCopyingWaitingRoomLink(true);
    try {
      await navigator.clipboard.writeText(shortUrl);
      setWaitingRoomLinkCopied(true);
      enqueueSnackbar('Waiting room link copied.', { variant: 'success' });
      window.setTimeout(() => setWaitingRoomLinkCopied(false), 1500);
    } catch {
      enqueueSnackbar('Could not copy waiting room link.', { variant: 'error' });
    } finally {
      setIsCopyingWaitingRoomLink(false);
    }
  }, [enqueueSnackbar, waitingRoom.link?.short_url]);

  const handleAdmitWaitingRoomEntry = useCallback(
    async (entry: WaitingRoomEntry) => {
      if (!session.encounterId) {
        enqueueSnackbar(`Open an encounter before admitting a ${subjectLanguage.singular}.`, {
          variant: 'warning',
        });
        return;
      }

      try {
        await waitingRoom.admitPatient(entry.waiting_room_queue_entry_id, session.encounterId);
        dispatch(clearWaitingRoomLaunchIntent());
        enqueueSnackbar(`${entry.patient_name} was added to the call.`, { variant: 'success' });
      } catch (error) {
        SentryService.captureException(error, {
          tags: { component: 'telehealth_waiting_room_in_call_admit' },
        });
        await waitingRoom.refreshQueue().catch(() => undefined);
        enqueueSnackbar(getWaitingRoomAdmitErrorMessage(error, subjectLanguage), {
          variant: 'error',
        });
      }
    },
    [dispatch, enqueueSnackbar, session.encounterId, subjectLanguage, waitingRoom],
  );

  const requestRemoveWaitingRoomEntry = useCallback((entry: WaitingRoomEntry) => {
    setEntryPendingRemoval(entry);
  }, []);

  const handleConfirmRemoveWaitingRoomEntry = useCallback(
    async (request: ProviderWaitingRoomRemoveRequest) => {
      if (!entryPendingRemoval) {
        return;
      }

      try {
        await waitingRoom.removeQueueEntry(
          entryPendingRemoval.waiting_room_queue_entry_id,
          request,
        );
        enqueueSnackbar(`${entryPendingRemoval.patient_name} was removed from the waiting room.`, {
          variant: 'success',
        });
        setEntryPendingRemoval(null);
      } catch (error) {
        SentryService.captureException(error, {
          tags: { component: 'telehealth_waiting_room_in_call_remove' },
        });
        await waitingRoom.refreshQueue().catch(() => undefined);
        enqueueSnackbar(getWaitingRoomRemoveErrorMessage(error, subjectLanguage), {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, entryPendingRemoval, subjectLanguage, waitingRoom],
  );

  const waitingRoomPanel = shouldShowWaitingRoomControls ? (
    <WaitingRoomPeoplePanel
      entries={waitingRoomEntries}
      providerAvailability={waitingRoom.providerAvailability}
      sseConnectionState={waitingRoom.sseConnectionState}
      link={waitingRoom.link}
      error={waitingRoom.error}
      inCallParticipants={inCallParticipants}
      variant="dark"
      density="compact"
      isSettingAvailability={waitingRoom.isSettingAvailability}
      isCopyingLink={isCopyingWaitingRoomLink}
      linkCopied={waitingRoomLinkCopied}
      defaultExpandedEntryId={defaultExpandedWaitingRoomEntryId}
      entryExpandedContent={entry => (
        <WaitingRoomEntryActionButtons
          variant="dark"
          primaryLabel={getAdmitActionLabel(entry)}
          primaryIcon={<VideocamIcon fontSize="small" />}
          onPrimaryAction={
            waitingRoom.isAdmitting || !isAdmittableEntry(entry)
              ? undefined
              : () => void handleAdmitWaitingRoomEntry(entry)
          }
          onRemoveFromWaitingRoom={
            waitingRoom.isRemoving ? undefined : () => requestRemoveWaitingRoomEntry(entry)
          }
        />
      )}
      onSetAvailability={handleSetWaitingRoomAvailability}
      onCopyLink={handleCopyWaitingRoomLink}
      onClose={() => setIsPeoplePanelOpen(false)}
    />
  ) : null;

  const handleToggleMic = useCallback(() => {
    const newState = !callState.localAudioEnabled;
    dailyCallService.setLocalAudio(newState);
    telehealth.micToggled({
      appointment_id: session.appointmentId,
      enabled: newState,
    });
  }, [callState.localAudioEnabled, dailyCallService, session.appointmentId, telehealth]);

  const handleToggleVideo = useCallback(() => {
    const newState = !callState.localVideoEnabled;
    dailyCallService.setLocalVideo(newState);
    telehealth.videoToggled({
      appointment_id: session.appointmentId,
      enabled: newState,
    });
  }, [callState.localVideoEnabled, dailyCallService, session.appointmentId, telehealth]);

  const handleShareToggle = useCallback(() => {
    if (isSharingScreen) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
    telehealth.screenShareToggled({
      appointment_id: session.appointmentId,
      enabled: !isSharingScreen,
    });
  }, [isSharingScreen, startScreenShare, stopScreenShare, session.appointmentId, telehealth]);

  const handleLayoutChange = useCallback(
    (choice: ManualLayoutChoice) => {
      layout.setManualLayoutChoice(choice);
      telehealth.layoutChanged({
        appointment_id: session.appointmentId,
        layout: choice,
        participant_count: callState.participantCount,
      });
    },
    [layout, session.appointmentId, callState.participantCount, telehealth],
  );

  const handleSendChat = useCallback(
    (text: string) => {
      sendChatMessage({
        type: 'chat',
        id: crypto.randomUUID(),
        text,
        senderName: localUserName || 'Provider',
        senderRole: 'provider',
        senderId: localSessionId ?? undefined,
        timestamp: Date.now(),
      });
    },
    [sendChatMessage, localUserName, localSessionId],
  );

  const showStatusRow = isSharingScreen || isRecording;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        bgcolor: 'grey.700',
        overflow: 'hidden',
        gap: 1.5,
        p: 1.5,
      }}
    >
      <WaitingRoomRemoveDialog
        open={Boolean(entryPendingRemoval)}
        entry={entryPendingRemoval}
        isSubmitting={waitingRoom.isRemoving}
        container={pipWindow?.document.body ?? popoverContainerEl ?? undefined}
        onClose={() => setEntryPendingRemoval(null)}
        onConfirm={handleConfirmRemoveWaitingRoomEntry}
      />

      {/* Status row — screen share + recording indicator */}
      {showStatusRow && (
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
            {isSharingScreen && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', bgcolor: 'grey.800', p: 1, borderRadius: 2 }}
              >
                <ScreenShareIcon sx={{ fontSize: 20, color: 'common.white' }} />
                <Typography sx={{ color: 'common.white', fontSize: 12 }}>
                  You are sharing your screen
                </Typography>
              </Stack>
            )}
          </Stack>

          {(isRecording || isRecordingStarting) && onPauseRecording && onResumeRecording && (
            <RecordingStatusBar
              isRecording={isRecording}
              isStarting={isRecordingStarting}
              isPaused={isRecordingPaused}
              onPause={onPauseRecording}
              onResume={onResumeRecording}
              hasRemoteParticipant={remoteParticipantIds.length > 0}
            />
          )}
        </Stack>
      )}

      {/* Video + chat row */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
        {canShowPeopleRail && waitingRoomPanel && (
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              minHeight: 0,
              mr: 1.5,
            }}
          >
            {waitingRoomPanel}
          </Box>
        )}

        {canShowPeopleOverlay && waitingRoomPanel && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 4,
              width: 'min(320px, calc(100% - 16px))',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.35)',
            }}
          >
            {waitingRoomPanel}
          </Box>
        )}

        {/* Layout area — same IIFE pattern as CallView */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: '8px',
            overflow: 'hidden',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(() => {
            if (layout.activeLayout === 'speaker') {
              return (
                <SpeakerView
                  mainStageSessionId={layout.mainStageSessionId}
                  sidebarParticipantIds={layout.sidebarParticipantIds}
                  activeSpeakerId={layout.activeSpeakerId}
                  localSessionId={layout.localSessionId}
                  isScreenShare={layout.hasRemoteScreenShare}
                  screenShareSessionId={layout.screenShareSessionId}
                  fillHeight
                />
              );
            }
            if (layout.activeLayout === 'grid') {
              return (
                <GridView
                  participantIds={layout.sidebarParticipantIds}
                  activeSpeakerId={layout.activeSpeakerId}
                  localSessionId={layout.localSessionId}
                  fillHeight
                />
              );
            }
            // spotlight (default for ≤2 participants)
            return (
              <VideoSpotlight
                remoteParticipantIds={remoteParticipantIds}
                localSessionId={localSessionId ?? ''}
                activeSpeakerId={layout.activeSpeakerId}
                fillHeight
                showLocalThumbnail={!hideSelfView}
              />
            );
          })()}
        </Box>

        {/* Chat panel sidebar */}
        <Box
          sx={{
            width: isChatPanelOpen ? 332 : 0,
            overflow: 'hidden',
            transition: 'width 200ms ease',
            flexShrink: 0,
            paddingLeft: isChatPanelOpen ? 1.5 : 0,
          }}
        >
          {isChatPanelOpen && (
            <ChatPanel
              messages={chatMessages}
              onSend={handleSendChat}
              onClose={() => dispatch(setChatPanelOpen(false))}
              isConnected={remoteParticipantIds.length > 0}
              localSessionId={localSessionId}
            />
          )}
        </Box>
      </Box>

      {/* Controls bar — 3-column grid matching CallView */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
        }}
      >
        {/* Left group: return to encounter + layout toggle */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', minWidth: 0, justifySelf: 'start' }}
        >
          {shouldShowWaitingRoomControls && (
            <Tooltip title={isPeoplePanelOpen ? 'Hide people' : 'Show people'}>
              <ButtonBase
                onClick={() => setIsPeoplePanelOpen(current => !current)}
                aria-label={isPeoplePanelOpen ? 'Hide people panel' : 'Show people panel'}
                sx={{
                  width: btnSize,
                  height: btnSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  bgcolor: isPeoplePanelOpen ? 'primary.main' : undefined,
                  '&:hover': {
                    bgcolor: isPeoplePanelOpen ? 'primary.dark' : 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                <Badge
                  badgeContent={waitingCount}
                  color="primary"
                  max={99}
                  invisible={isPeoplePanelOpen || waitingCount === 0}
                >
                  <PeopleIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
                </Badge>
              </ButtonBase>
            </Tooltip>
          )}

          {!isPipWindow && onClose && (
            <Tooltip title="Return to encounter">
              <ButtonBase
                onClick={onClose}
                aria-label="Return to encounter"
                sx={{
                  width: btnSize,
                  height: btnSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
              </ButtonBase>
            </Tooltip>
          )}
          {layout.showLayoutToggle && (
            <LayoutToggle layout={layout.manualLayoutChoice} onChange={handleLayoutChange} />
          )}
        </Stack>

        {/* Center group: mic, camera, share, end call */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: compactControls ? 1 : 1.5,
            padding: compactControls ? 0.75 : 1,
            borderRadius: 2,
            backgroundColor: 'grey.800',
          }}
        >
          {/* Mic split button */}
          <SplitMediaButton
            compact={compactControls}
            icon={<MicIcon sx={{ color: 'common.white', fontSize: iconSize }} />}
            inactiveIcon={<MicOffIcon color="error" sx={{ fontSize: iconSize }} />}
            isActive={callState.localAudioEnabled}
            onToggle={handleToggleMic}
            ariaLabel={callState.localAudioEnabled ? 'Mute microphone' : 'Unmute microphone'}
            tooltip={callState.localAudioEnabled ? 'Mute' : 'Unmute'}
            isDropdownOpen={activeDropdown === 'mic'}
            onDropdownOpenChange={open => setActiveDropdown(open ? 'mic' : null)}
            popoverContainer={pipWindow?.document.body ?? popoverContainerEl ?? undefined}
            dropdownContent={
              <MicDropdown
                audioInputDeviceService={audioInputDeviceService}
                isRecording={isRecording}
                session={session}
                setSelectedMicrophone={setSelectedMicrophone}
                onClose={() => setActiveDropdown(null)}
              />
            }
          />

          {/* Camera split button */}
          <SplitMediaButton
            compact={compactControls}
            icon={<VideocamIcon sx={{ color: 'common.white', fontSize: iconSize }} />}
            inactiveIcon={<VideocamOffIcon color="error" sx={{ fontSize: iconSize }} />}
            isActive={callState.localVideoEnabled}
            onToggle={handleToggleVideo}
            ariaLabel={callState.localVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
            tooltip={callState.localVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
            isDropdownOpen={activeDropdown === 'camera'}
            onDropdownOpenChange={open => setActiveDropdown(open ? 'camera' : null)}
            popoverContainer={pipWindow?.document.body ?? popoverContainerEl ?? undefined}
            dropdownContent={
              <CameraDropdown
                isRecording={isRecording}
                session={session}
                onClose={() => setActiveDropdown(null)}
              />
            }
          />

          {/* Screen share toggle */}
          <Tooltip
            title={
              someoneElseSharing
                ? 'Another participant is sharing'
                : isSharingScreen
                  ? 'Stop sharing'
                  : 'Share screen'
            }
          >
            <ButtonBase
              onClick={handleShareToggle}
              aria-label={isSharingScreen ? 'Stop screen share' : 'Share screen'}
              disabled={someoneElseSharing}
              sx={{
                width: btnSize,
                height: btnSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              {isSharingScreen ? (
                <StopScreenShareIcon sx={{ fontSize: iconSize, color: 'error.main' }} />
              ) : (
                <ScreenShareIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
              )}
            </ButtonBase>
          </Tooltip>

          {/* Chat toggle */}
          <Tooltip title={isChatPanelOpen ? 'Close chat' : 'Chat'}>
            <ButtonBase
              onClick={() => dispatch(toggleChatPanel())}
              aria-label={isChatPanelOpen ? 'Close chat' : 'Open chat'}
              sx={{
                width: btnSize,
                height: btnSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                bgcolor: isChatPanelOpen ? 'primary.main' : undefined,
                '&:hover': {
                  bgcolor: isChatPanelOpen ? 'primary.dark' : 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <Badge
                badgeContent={chatUnreadCount}
                color="error"
                max={99}
                invisible={chatUnreadCount === 0 || isChatPanelOpen}
              >
                <ChatIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
              </Badge>
            </ButtonBase>
          </Tooltip>

          <Tooltip title="Leave call">
            <ButtonBase
              onClick={onLeave}
              aria-label="Leave call"
              sx={{
                width: btnSize,
                height: btnSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'error.main',
                color: 'common.white',
                borderRadius: 1,
                '&:hover': { bgcolor: 'error.dark' },
              }}
            >
              <LogoutIcon sx={{ fontSize: iconSize }} />
            </ButtonBase>
          </Tooltip>
        </Box>

        {/* Right group: fullscreen + PiP */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', minWidth: 0, justifySelf: 'end' }}
        >
          {!isPipWindow && onToggleFullscreen && (
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <ButtonBase
                onClick={onToggleFullscreen}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                sx={{
                  width: btnSize,
                  height: btnSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                {isFullscreen ? (
                  <FullscreenExitIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
                ) : (
                  <FullscreenIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
                )}
              </ButtonBase>
            </Tooltip>
          )}
          {isPipWindow ? (
            <Tooltip title="Close picture-in-picture">
              <ButtonBase
                onClick={onClose}
                aria-label="Close picture-in-picture"
                sx={{
                  width: btnSize,
                  height: btnSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <PipExitIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
              </ButtonBase>
            </Tooltip>
          ) : isPipSupported && onEnterPip ? (
            <Tooltip title="Picture-in-picture">
              <ButtonBase
                onClick={onEnterPip}
                aria-label="Picture-in-picture"
                sx={{
                  width: btnSize,
                  height: btnSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <PipIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
              </ButtonBase>
            </Tooltip>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}
